<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  installment: { type: Object, default: null },
  rateio: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['download-sheet']);

const { t } = useI18n();
const cents = value => Number(value?.amount_cents || 0);
const totalCents = computed(() => cents(props.rateio.valor_total));
const transferredCents = computed(() =>
  Math.min(cents(props.rateio.valor_transferido), totalCents.value)
);
const progress = computed(() =>
  totalCents.value > 0
    ? Math.min(
        100,
        Math.round((transferredCents.value / totalCents.value) * 100)
      )
    : 0
);
const installmentLabel = computed(() => {
  const number = props.installment?.parcela?.numero_parcela;
  const total = props.installment?.total_parcelas;

  if (!number || !total || total === 1)
    return t('REAL_ESTATE_FINANCE.ALLOCATION.INTEGRAL');

  return t('REAL_ESTATE_FINANCE.ALLOCATION.INSTALLMENT', { number, total });
});
</script>

<template>
  <section
    data-testid="finance-installment-allocation"
    class="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(14rem,1fr)]"
  >
    <div
      class="rounded-lais border border-lais-border-soft bg-white p-5 shadow-lais-card"
    >
      <div class="flex flex-wrap items-start justify-between gap-3">
        <p class="text-sm font-semibold text-lais-text">
          {{ t('REAL_ESTATE_FINANCE.ALLOCATION.TOTAL') }}
        </p>
        <div class="flex flex-wrap items-center justify-end gap-2">
          <span
            class="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700"
          >
            {{ installmentLabel }}
          </span>
          <button
            v-if="
              installment?.parcela?.planilha?.disponivel &&
              installment?.parcela?.planilha?.pode_baixar
            "
            type="button"
            class="rounded-lais border border-lais-primary px-3 py-1.5 text-xs font-semibold text-lais-primary hover:bg-lais-primary/5"
            @click="emit('download-sheet', installment.parcela.planilha)"
          >
            {{ t('REAL_ESTATE_FINANCE.ALLOCATION.VIEW_SHEET') }}
          </button>
          <span
            v-else-if="!installment?.parcela?.planilha?.disponivel"
            class="rounded-lais bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700"
          >
            {{ t('REAL_ESTATE_FINANCE.ALLOCATION.SHEET_PENDING') }}
          </span>
        </div>
      </div>
      <p class="mt-5 font-redHatDisplay text-3xl font-bold text-lais-text">
        {{
          rateio.valor_total?.amount ||
          t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY')
        }}
      </p>
      <progress
        class="mt-5 h-2 w-full accent-lais-primary"
        :value="progress"
        max="100"
      />
      <div
        class="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-lais-text-muted"
      >
        <span>{{
          rateio.valor_transferido?.amount ||
          t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY')
        }}</span>
        <span>{{ t('REAL_ESTATE_FINANCE.ALLOCATION.OF') }}</span>
        <span>{{
          rateio.valor_total?.amount ||
          t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY')
        }}</span>
      </div>
      <p class="sr-only" aria-live="polite">
        {{ t('REAL_ESTATE_FINANCE.ALLOCATION.PROGRESS', { value: progress }) }}
      </p>
    </div>

    <div
      class="rounded-lais border border-lais-border-soft bg-white p-5 shadow-lais-card"
    >
      <p class="text-sm font-semibold text-lais-text">
        {{ t('REAL_ESTATE_FINANCE.ALLOCATION.REMAINING') }}
      </p>
      <p class="mt-5 font-redHatDisplay text-3xl font-bold text-lais-success">
        {{
          rateio.valor_restante?.amount ||
          t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY')
        }}
      </p>
    </div>
  </section>
</template>
